/* eslint-disable prefer-const */
import {
  compose, lifecycle, withContext, withHandlers, withProps,
} from 'recompose';
import { connect } from 'react-redux';
import {
  curry,
  path,
  pathOr,
  without,
  append,
  objOf,
  prop,
  not,
  pipe,
  pick, keys, reduce,
} from 'ramda';
import PropTypes from 'prop-types';
import { withRouter } from 'react-router';

import { PRELOADER_DIMENSION } from '../../constants/ui';
import {
  withRefs, preloaderWhileLoading, withUrlParams, withFilters, withAgendaColumnSwipe,
} from '../../utils/enchancers';
import { getArrayFromEntities } from '../../utils/helpers/uiHelpers';
import {
  setFilterForSingleParam,
  checkIfValueIncluded,
  filterAndTransformCondition,
  handlerFilter,
  makeNewFilterValue,
  filterParamKey,
  updateFilterAndUrlParams,
} from '../../utils/helpers/uiComponentHelpers/filters';
import { projectsSelectors } from '../../state/projects';

import { projectActions, projectSelectors } from '../../state/project';
import { uiActions } from '../../state/ui';

import AgendaBoard from './agendaBoard';
import { TITLES_CONSTANTS } from '../../constants/titles';
import { SPRINT_STATUS } from '../../constants/sprints';
import { STATUS } from '../../constants/projects';

const mapStateToProps = (state, { match: { params: { projectId } } }) => ({
  agendaStatusesList: pathOr([], ['project', projectId, 'result', 'statuses'], state),
  isAgendaRequestPending: projectSelectors.getAgendaTasksRequestPending(state),
  tasks: projectSelectors.getProjectSprintTasks(state)(projectId),
  sprintsEntities: projectSelectors.getSprintsEntities(state)(projectId),
  project: projectsSelectors.getProject(state)(projectId),
});

const mapDispatchToProps = ({
  getTasksRequest: projectActions.getAgendaTasksRequest,
  getTasksBySprint: projectActions.getAgendaTasksBySprintRequest,
  getUsersWithTasks: projectActions.getUsersWithTasksRequest,
  setFilter: uiActions.setFilter,
  setCurrentPage: uiActions.setPage,
});

const changeParamKey = (e) => {
  let [key, value] = e;
  if (key === 'sprint') {
    key = 'sprint_id';
  }
  if (value === null) {
    value = 'null';
  }
  return ({ field: key, value });
};

const onSelectSprintHandler = ({
  setFilters, onSetUrlParam, mergeFilters,
}) => (item) => {
  const sprint = pathOr(null, ['val', 'id'], item);
  return setFilterForSingleParam(mergeFilters, setFilters, onSetUrlParam, 'sprint', sprint);
};

const onResetSprintHandler = ({ setFilters, onResetUrlParam, mergeFilters }) => () => {
  setFilterForSingleParam(mergeFilters, setFilters, onResetUrlParam, 'sprint', null);
};

const onSelectUserHandler = ({
  onSetUrlParam, onResetUrlParam, setFilters, getFilter, mergeFilters,
}) => (userId) => {
  const selectedAssignee = getFilter(null, 'assignee');

  const makeAssigneeFilter = makeNewFilterValue(mergeFilters, 'assignee');
  const addAssigneeFilterAndUrl = updateFilterAndUrlParams(setFilters, onSetUrlParam);
  const resetAssigneeFilterAndUrl = updateFilterAndUrlParams(setFilters, onResetUrlParam);
  const objOfAssignee = objOf('assignee');

  const addAssignee = curry((assignees, assignee) => addAssigneeFilterAndUrl(
    makeAssigneeFilter(append(assignee))(assignees),
    objOfAssignee([assignee]),
  ));

  const resetAssignee = curry((assignees, assignee) => resetAssigneeFilterAndUrl(
    makeAssigneeFilter(without([assignee]))(assignees),
    objOfAssignee([assignee]),
  ));

  return handlerFilter(
    checkIfValueIncluded(selectedAssignee),
    resetAssignee(selectedAssignee),
    addAssignee(selectedAssignee),
    userId,
  );
};

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps),
  withRefs(),
  withUrlParams({ params: { concat: ['assignee'] } }),
  withFilters({
    initial: ({ getUrlParam }) => ({
      sprint: getUrlParam(['sprint']),
      assignee: getUrlParam(['assignee'], []),
    }),
  }),
  withProps(({
    match, getTasksRequest, project, sprintsEntities,
  }) => ({
    projectId: path(['params', 'projectId'], match),
    getTasksWithFilters: ({ filters }) => {
      const projectId = path(['params', 'projectId'], match);
      const where = filterAndTransformCondition(filterParamKey, changeParamKey, filters);
      getTasksRequest({ projectId, where });
    },
    sprints: getArrayFromEntities(reduce((accum, key) => (sprintsEntities[key].status === SPRINT_STATUS.ACTIVE ? { ...accum, [key]: sprintsEntities[key] } : accum), {}, keys(sprintsEntities))),
    isProjectCompleted: project.status === STATUS.COMPLETED,
  })),
  withContext({
    isAgendaRequestPending: PropTypes.bool.isRequired,
  }, pick(['isAgendaRequestPending'])),
  withHandlers({
    onSelectSprint: onSelectSprintHandler,
    onSelectUser: onSelectUserHandler,
    onResetSprint: onResetSprintHandler,
  }),
  lifecycle({
    componentDidMount() {
      const {
        getTasksWithFilters, projectId, getUrlParams, getUsersWithTasks, project,
      } = this.props;
      const filters = getUrlParams();
      this.props.setCurrentPage({ page: TITLES_CONSTANTS.AGENDA, mainTitle: project.title });
      getTasksWithFilters({ filters });
      getUsersWithTasks({ projectId });
    },
    componentDidUpdate(prevProps) {
      const { location, getTasksWithFilters, filters } = this.props;
      if (prevProps.location.search !== location.search) {
        getTasksWithFilters({ filters });
      }
    },
  }),
  preloaderWhileLoading({
    dimension: PRELOADER_DIMENSION.MIDDLE,
    alignContainerCenter: true,
    isLoading: pipe(
      prop('isAgendaRequestPending'),
      not,
    ),
  }),
  withAgendaColumnSwipe({ range: 50, boardList: [1, 2, 3] }),
);

export default enhance(AgendaBoard);
